import { useState, useRef, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Mic, Square, Loader2, RotateCcw, Sparkles, TriangleAlert, ListChecks, BookOpen, Lock } from "lucide-react";
import api from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import { AudioPlayer } from "@/components/AudioPlayer";
import { SurahReader } from "@/components/SurahReader";
import { GamifiedButton } from "@/components/GamifiedButton";
import { ProgressRing } from "@/components/ProgressRing";
import { Loading } from "@/components/Feedback";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSeo } from "@/hooks/useSeo";
import { cn } from "@/lib/utils";

export default function Recite() {
  useSeo("Réciter", "Enregistre ta récitation du Coran, verset par verset ou sourate entière, et reçois une évaluation avec score et conseils personnalisés.");
  const { refresh } = useAuth();
  const qc = useQueryClient();
  const [mode, setMode] = useState("verse"); // verse | surah
  const [surahNum, setSurahNum] = useState(1);
  const [ayahIdx, setAyahIdx] = useState(0);
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [seconds, setSeconds] = useState(0);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const { data: surahs } = useQuery({ queryKey: ["surahs"], queryFn: async () => (await api.get("/surahs")).data });
  const { data: surah, isLoading } = useQuery({ queryKey: ["surah", surahNum], queryFn: async () => (await api.get(`/surahs/${surahNum}`)).data });
  const { data: status } = useQuery({
    queryKey: ["surah-status", surahNum],
    queryFn: async () => (await api.get(`/surahs/${surahNum}/status`)).data,
  });

  const ayahs = surah?.ayahs || [];
  const ayah = ayahs[ayahIdx];

  const reset = useCallback(() => {
    setAudioBlob(null); setResult(null); setSeconds(0);
    setAudioUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
  }, []);

  useEffect(() => { setAyahIdx(0); reset(); }, [surahNum, reset]);
  useEffect(() => { reset(); }, [ayahIdx, mode, reset]);
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setResult(null);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      toast.error("Micro inaccessible. Autorise l'accès au microphone.");
    }
  };

  const stopRec = () => {
    mediaRef.current?.stop();
    setRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const analyze = async () => {
    if (!audioBlob) return;
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("audio", audioBlob, "recitation.webm");
      fd.append("surah_number", surahNum);
      fd.append("language", "fr");
      let data;
      if (mode === "surah") {
        ({ data } = await api.post("/recitation/analyze-surah", fd, { headers: { "Content-Type": "multipart/form-data" } }));
        toast.success("Récitation de la sourate analysée ! +50 points 🎉");
      } else {
        fd.append("ayah_number", ayah.number_in_surah);
        ({ data } = await api.post("/recitation/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } }));
        toast.success("Analyse terminée ! +25 points ⭐");
      }
      setResult(data);
      await refresh();
      qc.invalidateQueries({ queryKey: ["progress-summary"] });
      qc.invalidateQueries({ queryKey: ["surah-status", surahNum] });
      qc.invalidateQueries({ queryKey: ["gamification"] });
    } catch {
      toast.error("Échec de l'analyse.");
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-black text-slate-800">Réciter</h1>
        <p className="text-slate-500">Enregistre ta récitation et reçois une évaluation.</p>
      </div>

      <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-2xl px-4 py-3 text-amber-700 text-sm font-semibold">
        <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0" />
        <span>Analyse IA en mode démonstration (moteur ASR simulé). L'architecture est prête pour brancher un vrai moteur (Whisper, Google, Azure).</span>
      </div>

      {/* Mode toggle */}
      <div className="inline-flex bg-slate-100 rounded-2xl p-1" data-testid="recite-mode-toggle">
        <button data-testid="recite-mode-verse" onClick={() => setMode("verse")}
          className={cn("flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-colors",
            mode === "verse" ? "bg-white text-primary shadow-sm" : "text-slate-500")}>
          <ListChecks className="w-4 h-4" /> Par verset
        </button>
        <button data-testid="recite-mode-surah" onClick={() => setMode("surah")}
          className={cn("flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-colors",
            mode === "surah" ? "bg-white text-primary shadow-sm" : "text-slate-500")}>
          <BookOpen className="w-4 h-4" /> Sourate complète
        </button>
      </div>

      {/* selectors */}
      <div className={cn("grid gap-4", mode === "verse" ? "sm:grid-cols-2" : "grid-cols-1")}>
        <Select value={String(surahNum)} onValueChange={(v) => setSurahNum(Number(v))}>
          <SelectTrigger data-testid="recite-surah-select" className="px-4 py-6 rounded-2xl border-2 border-slate-200 font-bold text-base">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white max-h-72">
            {(surahs || []).map((s) => <SelectItem key={s.number} value={String(s.number)} className="font-semibold">{s.number}. {s.name_fr}</SelectItem>)}
          </SelectContent>
        </Select>
        {mode === "verse" && (
          <Select value={String(ayahIdx)} onValueChange={(v) => setAyahIdx(Number(v))}>
            <SelectTrigger data-testid="recite-ayah-select" className="px-4 py-6 rounded-2xl border-2 border-slate-200 font-bold text-base">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white max-h-72">
              {ayahs.map((a) => <SelectItem key={a.number_in_surah} value={String(ayahs.indexOf(a))} className="font-semibold">Verset {a.number_in_surah}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {isLoading || (mode === "verse" && !ayah) ? <Loading /> : (
        <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
          {mode === "verse" ? (
            <>
              <p className="text-center font-bold text-slate-500 mb-4">Récite le verset</p>
              <p className="font-arabic text-4xl sm:text-5xl leading-[2.4] text-slate-900 text-right mb-4" data-testid="recite-arabic">{ayah.text_ar}</p>
              <AudioPlayer src={ayah.audio_url} className="mb-6" />
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-black text-slate-800">{surah.name_fr}</h2>
                  <p className="text-sm text-slate-400">Récitation de la sourate entière</p>
                </div>
                {status && (
                  <span className={cn("px-3 py-1 rounded-full text-sm font-extrabold",
                    status.all_learned ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-500")} data-testid="surah-learn-status">
                    {status.learned}/{status.total} versets appris
                  </span>
                )}
              </div>
              <SurahReader surah={surah} showTranslation={false} />
              {status && !status.all_learned && (
                <div className="flex items-start gap-2 bg-primary/5 border border-primary/10 rounded-2xl px-4 py-3 mt-4 text-primary text-sm font-semibold" data-testid="surah-lock-hint">
                  <Lock className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>Apprends d'abord tous les versets de cette sourate (onglet Apprendre) pour débloquer la récitation complète.</span>
                </div>
              )}
              {status?.full_recitation_best != null && (
                <div className="text-sm font-bold text-slate-500 mt-3">Meilleur score complet : <span className="text-emerald-600">{status.full_recitation_best}%</span></div>
              )}
            </>
          )}

          {/* Recorder (shared) */}
          <div className="flex flex-col items-center gap-3 py-4 mt-2">
            {!recording ? (
              <button onClick={startRec} data-testid="record-btn"
                className="w-20 h-20 rounded-full bg-red-500 text-white flex items-center justify-center shadow-[0_6px_0_#dc2626] active:translate-y-1.5 active:shadow-none transition-all">
                <Mic className="w-8 h-8" />
              </button>
            ) : (
              <button onClick={stopRec} data-testid="stop-btn"
                className="w-20 h-20 rounded-full bg-slate-800 text-white flex items-center justify-center animate-pulse shadow-lg">
                <Square className="w-7 h-7 fill-current" />
              </button>
            )}
            <span className="font-extrabold text-slate-500 tabular-nums">
              {recording ? `● ${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}` : "Appuie pour enregistrer"}
            </span>
          </div>

          {audioUrl && !recording && (
            <div className="mt-4 space-y-4">
              <audio src={audioUrl} controls className="w-full" data-testid="recorded-audio" />
              <div className="flex gap-3">
                <GamifiedButton variant="ghost" onClick={reset} data-testid="rerecord-btn"><RotateCcw className="w-5 h-5" /> Refaire</GamifiedButton>
                <GamifiedButton className="flex-1" onClick={analyze}
                  disabled={analyzing || (mode === "surah" && status && !status.all_learned)} data-testid="analyze-btn">
                  {analyzing ? <><Loader2 className="w-5 h-5 animate-spin" /> Analyse…</> : <><Sparkles className="w-5 h-5" /> {mode === "surah" ? "Analyser la sourate" : "Analyser ma récitation"}</>}
                </GamifiedButton>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Result */}
      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)]" data-testid="analysis-result">
            <h3 className="font-black text-slate-800 text-xl mb-6">Analyse de ma récitation</h3>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <ProgressRing value={result.score} size={130} color={result.score >= 85 ? "hsl(142 71% 45%)" : "hsl(48 89% 50%)"} label={`${result.score}%`} sub="Score" />
              <div className="flex-1 space-y-3 w-full">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-bold text-slate-500">Mots correctement récités</span>
                  <span className="font-extrabold text-emerald-600">{result.correct_words?.length || 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-bold text-slate-500">Erreurs détectées</span>
                  <span className="font-extrabold text-red-500">{result.errors?.length || 0}</span>
                </div>
                {result.errors?.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {result.errors.map((e) => (
                      <span key={e.index} className="font-arabic text-lg bg-red-50 text-red-500 px-3 py-1 rounded-xl">{e.word}</span>
                    ))}
                  </div>
                )}
                <div className="bg-primary/5 border border-primary/10 rounded-2xl p-4 text-sm text-slate-600 font-semibold">
                  💡 {result.advice}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
